import { createEvent } from 'effector';
import { track } from './amplitude';
import {
  SCENE_ENTERED,
  SCENE_EXITED,
  SCENE_PREVIEW_LOADED,
  SCENE_FULLY_LOADED,
  SCENE_LOAD_FAILED,
  ASSISTANT_MESSAGE_SENT,
} from './events';
import { $sceneId } from '../state/appState';
import {
  sceneLoadingStarted,
  scenePreviewLoadingFinished,
  sceneLoadingFinished,
  sceneLoadingFailed,
} from '../state/sceneLoading';
import { assistantPromptSubmitted } from '../state/assistantHud';
import { errorMessage } from '../utils/errorMessage';
import { isOnline } from '../utils/isOnline';

export const sceneEntered = createEvent<string>();
export const sceneExited = createEvent<string | null>();

let sceneLoadStartTime: number | null = null;
// Timestamp когда текущая сцена завершила загрузку (= `sceneLoadingFinished`).
// На `sceneExited` вычисляем `dwell_ms = now - sceneReadyAt`. Null означает,
// что юзер ушёл до того, как сцена дошла до ready (включая случай ошибки
// загрузки — отдельно ловится через `Scene Load Failed`).
let sceneReadyAt: number | null = null;

export const startSceneTracking = () => {
  sceneEntered.watch((sceneId) => {
    track(SCENE_ENTERED, { scene_id: sceneId });
  });

  sceneExited.watch((previousSceneId) => {
    const dwellMs =
      sceneReadyAt !== null
        ? Math.round(performance.now() - sceneReadyAt)
        : null;
    track(SCENE_EXITED, {
      scene_id: previousSceneId,
      dwell_ms: dwellMs,
    });
    sceneReadyAt = null;
  });

  sceneLoadingStarted.watch(() => {
    sceneLoadStartTime = performance.now();
    // Новая загрузка → сбрасываем готовность предыдущей (если по каким-то
    // причинам она ещё лежала); следующий sceneLoadingFinished переустановит.
    sceneReadyAt = null;
  });

  scenePreviewLoadingFinished.watch(() => {
    if (sceneLoadStartTime === null) return;
    const durationMs = Math.round(performance.now() - sceneLoadStartTime);
    track(SCENE_PREVIEW_LOADED, {
      duration_ms: durationMs,
      scene_id: $sceneId.getState(),
    });
  });

  sceneLoadingFinished.watch(() => {
    if (sceneLoadStartTime === null) return;
    const durationMs = Math.round(performance.now() - sceneLoadStartTime);
    track(SCENE_FULLY_LOADED, {
      duration_ms: durationMs,
      duration_sec: +(durationMs / 1000).toFixed(2),
      scene_id: $sceneId.getState(),
    });
    sceneLoadStartTime = null;
    sceneReadyAt = performance.now();
  });

  sceneLoadingFailed.watch(({ stage, error }) => {
    const durationMs =
      sceneLoadStartTime !== null
        ? Math.round(performance.now() - sceneLoadStartTime)
        : null;
    track(SCENE_LOAD_FAILED, {
      scene_id: $sceneId.getState(),
      duration_ms: durationMs,
      stage,
      error_message: errorMessage(error),
      online: isOnline(),
    });
    sceneLoadStartTime = null;
  });

  assistantPromptSubmitted.watch(() => {
    track(ASSISTANT_MESSAGE_SENT, {
      scene_id: $sceneId.getState(),
    });
  });
};
